'use client';

import type { ButtonClickEvent } from '@hc-frontend/core-ui';
import { Button, useEventBus, useTrackingContexts } from '@hc-frontend/core-ui';
import type {
  SendMetricEvent,
  SendMetricEventTarget,
} from '@hc-frontend/shells-shared';
import { useEffect, useRef, useState } from 'react';

import { TypConnectMeHolder } from '../../molecules/typ-connect-me-holder/typ-connect-me-holder';
import type { ConnectMeButtonProps } from './connect-me-button.types';

export function ConnectMeButton({
  children,
  experience,
  callType,
  phoneNumber,
  destinationPhoneNumber,
  placement,
  sessionId,
  product,
  isSendingDisplayMetric = true,
  onClick,
  advertiser,
  ...props
}: ConnectMeButtonProps) {
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();
  const buttonId = 'connect-me-button';

  const [buttonWasClicked, setButtonWasClicked] = useState(false);

  const clickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
      id: buttonId,
      topic: 'metric.client.message',
      source: event.currentTarget,
      metric: {
        name: 'connect-me',
        tags: {
          action: 'Click Call',
          experience,
          placement,
          product,
        },
      },
    });

    eventBus.publish<ButtonClickEvent>({
      id: buttonId,
      topic: 'core.ui.button.click',
      source: event.currentTarget,
      contexts,
    });

    onClick?.(event);

    setButtonWasClicked(true);
  };

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (ref.current && isSendingDisplayMetric) {
      eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
        id: buttonId,
        topic: 'metric.client.message',
        source: ref.current,
        metric: {
          name: 'connect-me',
          tags: {
            action: 'Display',
            experience,
            placement,
            product,
          },
        },
      });
    }
  }, [
    buttonId,
    eventBus,
    experience,
    placement,
    product,
    isSendingDisplayMetric,
  ]);

  return buttonWasClicked ? (
    <TypConnectMeHolder
      experience={experience}
      phoneNumber={phoneNumber}
      destinationPhoneNumber={destinationPhoneNumber}
      placement={placement}
      sessionId={sessionId}
      product={product}
      advertiser={advertiser}
    />
  ) : (
    <Button
      ref={ref}
      onClick={clickHandler}
      data-call-call-type={callType}
      data-call-placement={placement}
      data-call-product={product}
      data-call-phone={phoneNumber}
      data-call-advertiser={advertiser}
      {...props}
    >
      {children}
    </Button>
  );
}
